import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageTitle } from '../../../_metronic/layout/core'
import { KTCard, KTCardBody } from '../../../_metronic/helpers'
import { KTIcon } from '../../../_metronic/helpers'
import { create_subcategory } from './api/subcategoriesApi'
import { get_all_categories } from '../projects/api/projectCategoriesApi'
import { toast } from 'react-toastify'
import { permissionCheck } from '../../helper-functions/PermissionCheck'

interface CategoryItem {
  id: number
  category_name: string
}

const AddSubcategoryPage: React.FC = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    subcategory_name: '',
    category_id: '',
    description: '',
    status: 1
  })
  const [categories, setCategories] = useState<CategoryItem[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingCategories, setIsLoadingCategories] = useState(false)
  const canCreateSubcategory = Boolean(permissionCheck('subcategory-create'))

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true)
        const res = await get_all_categories()
        const apiCats = res?.data?.all_categories || res?.data?.categories || res?.data || []
        const all: CategoryItem[] = (Array.isArray(apiCats) ? apiCats : []).map((c: any) => ({
          id: Number(c.id),
          category_name: c.category_name
        }))
        setCategories(all)
      } catch (error) {
        console.error('Error fetching categories:', error)
        toast.error('Failed to load categories')
      } finally {
        setIsLoadingCategories(false)
      }
    }
    fetchCategories()
  }, [])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.subcategory_name.trim()) {
      newErrors.subcategory_name = 'Subcategory name is required'
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!canCreateSubcategory) {
      toast.error('You do not have permission to create subcategories')
      return
    }

    if (validateForm()) {
      try {
        setIsSubmitting(true)
        const payload = {
          subcategory_name: formData.subcategory_name.trim(),
          category_id: Number(formData.category_id),
          description: formData.description.trim(),
          status: String(formData.status)
        }
        await create_subcategory(payload)
        toast.success('Subcategory created successfully')
        navigate('/subcategories')
      } catch (error) {
        console.error('Error creating subcategory:', error)
        toast.error('Failed to create subcategory')
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }


  if (!canCreateSubcategory) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>Add Subcategory</PageTitle>
        <div className='d-flex flex-column flex-lg-row'>
          <div className='flex-lg-row-fluid'>
            <KTCard>
              <KTCardBody>
                <div className='alert alert-warning mb-0'>
                  You do not have permission to create subcategories.
                </div>
              </KTCardBody>
            </KTCard>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Add Subcategory</PageTitle>
      <div className='d-flex flex-column flex-lg-row'>
        <div className='flex-lg-row-fluid'>
          <KTCard>
          <div className='card-header'>
            <div className='card-title'>
              <h3 className='fw-bold text-dark'>Add New Subcategory</h3>
            </div>
            <div className='card-toolbar'>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-active-color-primary'
                onClick={() => navigate('/subcategories')}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </button>
            </div>
          </div>
          <KTCardBody>
            <form onSubmit={handleSubmit}>
              <div className='row mb-6'>
                <div className='col-lg-6'>
                  <label className='col-form-label fw-bold fs-6'>
                    <span className='text-danger'>*</span>Subcategory Name
                  </label>
                  <input
                    type='text'
                    name='subcategory_name'
                    className={`form-control form-control-solid ${errors.subcategory_name ? 'is-invalid' : ''}`}
                    placeholder='Enter subcategory name'
                    value={formData.subcategory_name}
                    onChange={handleChange}
                  />
                  {errors.subcategory_name && <div className='invalid-feedback'>{errors.subcategory_name}</div>}
                </div>
                <div className='col-lg-6'>
                  <label className='col-form-label fw-bold fs-6'>Status</label>
                  <select
                    name='status'
                    className='form-select form-select-solid'
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-lg-12'>
                  <label className='col-form-label fw-bold fs-6'>
                    <span className='text-danger'>*</span>Category
                  </label>
                  <select
                    name='category_id'
                    className={`form-select form-select-solid ${errors.category_id ? 'is-invalid' : ''}`}
                    value={formData.category_id}
                    onChange={handleChange}
                    disabled={isLoadingCategories}
                  >
                    <option value=''>Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                    ))}
                  </select>
                  {errors.category_id && <div className='invalid-feedback'>{errors.category_id}</div>}
                  {isLoadingCategories && <div className='form-text'>Loading categories...</div>}
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-lg-12'>
                  <label className='col-form-label fw-bold fs-6'>
                    Description
                  </label>
                  <textarea
                    name='description'
                    className={`form-control form-control-solid ${errors.description ? 'is-invalid' : ''}`}
                    rows={3}
                    placeholder='Enter subcategory description'
                    value={formData.description}
                    onChange={handleChange}
                  />
                  {errors.description && <div className='invalid-feedback'>{errors.description}</div>}
                </div>
              </div>


              <div className='d-flex justify-content-end'>
                <button
                  type='button'
                  className='btn btn-light me-3'
                  onClick={() => navigate('/subcategories')}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <KTIcon iconName='check' className='fs-2' />
                      Save Subcategory
                    </>
                  )}
                </button>
              </div>
            </form>
          </KTCardBody>
        </KTCard>
        </div>
      </div>
    </>
  )
}

export { AddSubcategoryPage }

